import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Wraps any route that requires an authenticated user.
 * - While auth state is loading, shows a spinner (prevents flash of redirect).
 * - When unauthenticated, redirects to /auth and remembers the intended destination
 *   via the `redirect` query param so the user lands back here after sign-in.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    const redirectTarget = `${location.pathname}${location.search}${location.hash}`;
    const target = `/auth?returnUrl=${encodeURIComponent(redirectTarget)}`;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};
